import { create } from "zustand";

import type {
	Artifact,
	ArtifactEditStep,
	Brain,
	ChatMessage,
	ChatMessageWithUIState,
	Content,
	DocumentTitle,
	WebMode,
} from "@virtual-brain-lib/types";

import type { UploadFileItem } from "../../../upload-progress";

export type ChatStore = {
	inputValue: string;
	selectedBrains: Brain[];
	selectedFiles: DocumentTitle[];
	uploadedFiles: Content[];
	webMode: WebMode;
	brainCreation: {
		isCreated: boolean;
		brainId: string;
		contentCount: {
			total: number;
			processed: number;
		};
	};

	isUploadCardOpen: boolean;
	processingFiles: Record<string, UploadFileItem>;
	answeringByMessageId: Map<ChatMessage["id"], { isAnswering: boolean }>;
	artifactEditingState: Map<
		Artifact["id"],
		{
			isExploring: boolean;
			isEditing: boolean;
			completedSteps: Set<ArtifactEditStep>;
			currentStep?: ArtifactEditStep | undefined;
		}
	>;

	setIsUploadCardOpen: (value: boolean) => void;
	setProcessingFiles: (
		update:
			| Record<string, UploadFileItem>
			| ((prev: Record<string, UploadFileItem>) => Record<string, UploadFileItem>),
	) => void;
	setInputValue: (value: string) => void;
	selectBrains: (brains: Brain[]) => void;
	setSelectedFiles: (files: DocumentTitle[]) => void;
	setUploadedFiles: (files: Content[] | ((prev: Content[]) => Content[])) => void;
	setWebMode: (value: WebMode) => void;
	setBrainCreation: (brainCreation: {
		isCreated: boolean;
		brainId: string;
		contentCount: { total: number; processed: number };
	}) => void;

	setAnsweringMessage: (params: {
		messageId: ChatMessageWithUIState["id"];
		isAnswering: boolean;
	}) => void;

	setAnsweringMessages: (
		answeringMessages: Array<{
			messageId: ChatMessageWithUIState["id"];
			isAnswering: boolean;
		}>,
	) => void;

	setArtifactEditing: (params: {
		artifactId: Artifact["id"];
		isExploring: boolean;
		isEditing: boolean;
		completedSteps?: Set<ArtifactEditStep>;
		currentStep?: ArtifactEditStep | undefined;
	}) => void;

	reset: () => void;
};

export const useChatStore = create<ChatStore>((set, get) => ({
	inputValue: "",
	selectedBrains: [],
	selectedFiles: [],
	uploadedFiles: [],
	webMode: "auto",
	brainCreation: {
		isCreated: false,
		brainId: "",
		contentCount: {
			total: 0,
			processed: 0,
		},
	},
	isUploadCardOpen: true,
	processingFiles: {},
	answeringByMessageId: new Map(),
	artifactEditingState: new Map(),

	setIsUploadCardOpen(value) {
		set({ isUploadCardOpen: value });
	},
	setProcessingFiles(update) {
		set((state) => ({
			processingFiles: typeof update === "function" ? update(state.processingFiles) : update,
		}));
	},
	setInputValue(value) {
		set({ inputValue: value });
	},
	selectBrains(brains) {
		set({ selectedBrains: brains });
	},
	setSelectedFiles(files) {
		set({ selectedFiles: files });
	},
	setUploadedFiles(files) {
		set((state) => ({
			uploadedFiles: typeof files === "function" ? files(state.uploadedFiles) : files,
		}));
	},
	setWebMode(value) {
		set({ webMode: value });
	},
	setAnsweringMessage({ messageId, isAnswering }) {
		const map = new Map(get().answeringByMessageId);

		if (isAnswering) {
			map.set(messageId, { isAnswering });
		} else {
			map.delete(messageId);
		}

		set({
			answeringByMessageId: map,
		});
	},
	setAnsweringMessages(activities) {
		const map = new Map(get().answeringByMessageId);
		let hasChanged = false;

		for (const { messageId, isAnswering } of activities) {
			if (isAnswering) {
				if (!map.has(messageId)) {
					map.set(messageId, { isAnswering });
					hasChanged = true;
				}
			} else if (map.delete(messageId)) {
				hasChanged = true;
			}
		}

		if (!hasChanged) {
			return;
		}

		set({
			answeringByMessageId: map,
		});
	},
	setArtifactEditing({ artifactId, isExploring, isEditing, completedSteps, currentStep }) {
		const map = new Map(get().artifactEditingState);

		if (!isExploring && !isEditing) {
			map.delete(artifactId);
		} else {
			const current = map.get(artifactId);

			map.set(artifactId, {
				isExploring,
				isEditing,
				completedSteps: completedSteps ?? current?.completedSteps ?? new Set(),
				currentStep: currentStep ?? current?.currentStep,
			});
		}

		set({ artifactEditingState: map });
	},
	setBrainCreation(brainCreation) {
		set({ brainCreation: { ...get().brainCreation, ...brainCreation } });
	},

	reset() {
		set({
			inputValue: "",
			selectedBrains: [],
			selectedFiles: [],
			uploadedFiles: [],
			webMode: "auto",
			brainCreation: {
				isCreated: false,
				brainId: "",
				contentCount: {
					total: 0,
					processed: 0,
				},
			},
			processingFiles: {},
			answeringByMessageId: new Map(),
			artifactEditingState: new Map(),
		});
	},
}));
